/**
 * ChatResponseOptions component for the Indigo design system.
 *
 * Displays a header followed by a row of action buttons inside a chat
 * response bubble. Used when the assistant offers a small set of secondary
 * actions the user can take next (e.g. "View case", "Schedule appointment").
 *
 * Each option renders as a `Button` (default `secondary` / `sm`).
 *
 * @example
 * ```tsx
 * <ChatResponseOptions
 *   header="What would you like to do next?"
 *   options={[
 *     { label: "View case", icon: <Icon name="browser-window" size="s" />, onClick: ... },
 *     { label: "Schedule", icon: <Icon name="calendar" size="s" />, onClick: ... },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, ReactNode } from "react";
import { Button, ButtonProps } from "../../Button/Button";
import { Body } from "../../Typography/Body";
import "./ChatResponseOptions.css";

export interface ChatResponseOptionItem {
  /** Visible label for the option button. */
  label: string;
  /** Optional icon rendered to the left of the label. */
  icon?: ReactNode;
  /** Click handler. */
  onClick?: () => void;
  /** Override button variant (default `secondary`). */
  variant?: ButtonProps["variant"];
  /** When true, the option is rendered in a disabled state. */
  disabled?: boolean;
  /** Optional test id for the option button. */
  "data-testid"?: string;
}

export interface ChatResponseOptionsProps {
  /** Optional header rendered above the option row. */
  header?: string;
  /** List of options. */
  options: ChatResponseOptionItem[];
  /** Button size used for every option (default `sm`). */
  size?: ButtonProps["size"];
  /** Additional CSS class applied to the root. */
  className?: string;
  /** Test id for the root element. */
  "data-testid"?: string;
}

export const ChatResponseOptions = forwardRef<
  HTMLDivElement,
  ChatResponseOptionsProps
>(
  (
    { header, options, size = "sm", className, "data-testid": dataTestId },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={clsx("indigo-chat-response-options", className)}
        data-testid={dataTestId}
      >
        {header && (
          <Body
            as="span"
            size="xs"
            className="indigo-chat-response-options__header"
          >
            {header}
          </Body>
        )}
        <div className="indigo-chat-response-options__list">
          {options.map((option, index) => (
            <Button
              key={`${option.label}-${index}`}
              variant={option.variant ?? "secondary"}
              size={size}
              icon={option.icon}
              onClick={option.onClick}
              disabled={option.disabled}
              data-testid={option["data-testid"]}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>
    );
  },
);

ChatResponseOptions.displayName = "ChatResponseOptions";

export default ChatResponseOptions;
